import React from 'react';
import { motion } from 'framer-motion';
import { Shield, Award, Users, Target, CheckCircle } from 'lucide-react';

const About = () => {
  const values = [
    {
      icon: Shield,
      title: "Safety First",
      description: "Every decision we make prioritizes the safety and well-being of workers."
    },
    {
      icon: Award,
      title: "Excellence",
      description: "We maintain the highest standards in training, consulting, and service delivery."
    },
    {
      icon: Users,
      title: "Partnership",
      description: "We work hand in hand with clients to develop customized workforce solutions."
    },
    {
      icon: Target,
      title: "Results",
      description: "We focus on results and performance."
    }
  ];

  const achievements = [
    "Industry Consultants",
    "Workforce Development",
    "24/7 Support - Rapid response",
    "Services and Trainings"
  ];

  return (
    <section id="about" className="py-5 bg-white scroll-mt-40">
      <div className="container mx-auto px-4">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <motion.div
              className="inline-flex items-center gap-2 bg-orange-100 text-orange-600 px-4 py-2 rounded-full mb-6"
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
              viewport={{ once: true }}
            >
              <Shield className="w-4 h-4" />
              <span className="font-semibold">About MDX</span>

              {/* Title Workforce Academy*/}
              <div className="flex-1">
                <h1 className={`text-2xl font-bold text-white} `}
                  style={{ fontFamily: "'Montserrat', sans-serif", fontSize: '1.5rem' }}>
                  Workforce Academy
                </h1>
              </div>
            </motion.div>


            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Building Trust Through
              <span className="block bg-gradient-to-r from-orange-500 to-red-500 bg-clip-text text-transparent">
                Excellence
              </span>
            </h2>

            <p className="text-xl text-gray-600 mb-8 leading-relaxed">
              For over 15 years, MDX has been the trusted partner for companies seeking
              comprehensive workforce and safety solutions. We combine industry
              expertise with innovative solutions to create effective and result oriented environments.
            </p>

            <p className="text-gray-600 mb-8 leading-relaxed">
              Our team of certified industry consultants brings real-world industry
              experience to every client, consultation, and program we develop.
              We understand the unique challenges of the industry and provide
              practical, effective solutions.
            </p>

            {/* Achievements */}
            <div className="space-y-3 mb-8">
              {achievements.map((achievement, index) => (
                <motion.div
                  key={index}
                  className="flex items-center gap-3"
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 + 0.3 }}
                  viewport={{ once: true }}
                >
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                  <span className="text-gray-700 font-medium">{achievement}</span>
                </motion.div>
              ))}
            </div>

            <motion.button
              className="bg-gradient-to-r from-orange-500 to-red-500 text-white px-8 py-4 rounded-full font-bold text-lg hover:shadow-xl transition-all duration-300"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <div className="flex items-center gap-4">
                <a href="mailto:info@mdxsafetyinc.com" class="flex items-center gap-2 text-white hover:text-[#a1a09a] transition-colors">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                    <polyline points="22,6 12,13 2,6"></polyline>
                  </svg>
                  <span class="font-bold">Learn More About Us</span>
                </a>
              </div>

            </motion.button>
          </motion.div>

          {/* Visual */}
          <motion.div
            className="relative"
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
          >
            {/* Main Image */}
            <div className="relative rounded-3xl overflow-hidden shadow-2xl">
              <img
                src="https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=600&h=400&fit=crop"
                alt="Construction Safety Training"
                className="w-full h-96 object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
            </div>

            {/* Floating Stats */}
            <motion.div
              className="absolute -bottom-6 -left-6 bg-white rounded-2xl p-6 shadow-xl border border-gray-100"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              viewport={{ once: true }}
            >
              <div className="text-3xl font-bold text-orange-500 mb-1">75+</div>
              <div className="text-gray-600 font-semibold">Years Experience</div>
            </motion.div>

            <motion.div
              className="absolute -top-6 -right-6 bg-white rounded-2xl p-6 shadow-xl border border-gray-100"
              initial={{ opacity: 0, y: -20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              viewport={{ once: true }}
            >
              <div className="text-3xl font-bold text-green-500 mb-1">99%</div>
              <div className="text-gray-600 font-semibold">Safety Record</div>
            </motion.div>
          </motion.div>
        </div>

        {/* Values Section */}
        <motion.div
          className="mt-20"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-gray-900 mb-4">Our Core Values</h3>
            <p className="text-gray-600 max-w-2xl mx-auto">
              These principles guide everything we do and shape how we serve our clients.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => {
              const IconComponent = value.icon;

              return (
                <motion.div
                  key={index}
                  className="text-center group"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1, duration: 0.6 }}
                  viewport={{ once: true }}
                >
                  <motion.div
                    className="w-16 h-16 bg-gradient-to-br from-orange-500 to-red-500 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300"
                    whileHover={{ rotate: 360 }}
                    transition={{ duration: 0.6 }}
                  >
                    <IconComponent className="w-8 h-8 text-white" />
                  </motion.div>

                  <h4 className="text-xl font-bold text-gray-900 mb-3">{value.title}</h4>
                  <p className="text-gray-600 leading-relaxed">{value.description}</p>
                </motion.div>
              );
            })}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default About;